<template>
  <div class="flex h-screen overflow-hidden bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-300 font-sans transition-colors duration-200">
    <!-- Sidebar Component -->
    <AppSidebar
      v-if="!isLoginPage"
      :is-mobile-menu-open="isMobileMenuOpen"
      :is-sidebar-open="isSidebarOpen"
      @toggle-mobile-menu="toggleMobileMenu"
      @toggle-sidebar="toggleSidebar"
    />

    <!-- Main Content Wrapper -->
    <div class="flex-1 flex flex-col h-screen overflow-hidden relative w-full min-w-0">
      <!-- Header -->
      <AppHeader v-if="!isLoginPage" @toggle-mobile-menu="toggleMobileMenu(true)" />

      <!-- Main Content Area with responsive padding -->
      <main class="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 dark:bg-gray-900 transition-colors duration-200 safe-area-bottom">
        <!-- View Container with compact responsive padding -->
        <div class="min-h-full w-full max-w-none p-2 sm:p-3 md:p-4 lg:p-5">
          <router-view v-slot="{ Component }">
            <Transition name="page" mode="out-in">
              <component :is="Component" :key="$route.path" />
            </Transition>
          </router-view>
        </div>
      </main>

      <!-- Footer -->
      <footer
        v-if="!isLoginPage"
        class="bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border-t border-gray-200 dark:border-gray-800 py-3 px-6 transition-colors duration-200 shrink-0"
      >
        <div class="flex flex-col sm:flex-row justify-between items-center gap-2 text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wider">
          <div>
            Copyright &copy; {{ new Date().getFullYear() }} <span class="text-gray-700 dark:text-gray-200">Alkara Solution</span>
          </div>
          <div class="flex items-center gap-1">
            <span>Support by</span>
            <span class="opacity-20 hover:opacity-100 transition-opacity duration-500 cursor-default font-bold">CV.Alkara Lintas Persada</span>
          </div>
        </div>
      </footer>

      <!-- Alert Manager -->
      <AlertManager />

      <!-- Loading Overlay -->
      <Transition name="fade">
        <div
          v-if="isLoading"
          class="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <div class="bg-white dark:bg-gray-800 rounded-xl p-5 sm:p-6 shadow-xl max-w-xs w-full">
            <div class="flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400"></div>
              <span class="text-gray-700 dark:text-gray-300 font-medium text-center sm:text-left">Loading...</span>
            </div>
          </div>
        </div>
      </Transition>

      <!-- Demo Timer & Lockout -->
      <DemoTimerDisplay v-if="!isLoginPage" />
      <DemoLockoutScreen v-if="!isLoginPage" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import AppSidebar from './components/layout/AppSidebar.vue'
import AppHeader from './components/layout/AppHeader.vue'
import AlertManager from './components/ui/AlertManager.vue'
import DemoTimerDisplay from './components/demo/DemoTimerDisplay.vue'
import DemoLockoutScreen from './components/demo/DemoLockoutScreen.vue'
import { useThemeStore } from '@/stores/theme'
import { useContentProtection } from '@/composables/useContentProtection'

// Router instance
const router = useRouter()
const route = useRoute()

// Initialize Content Protection (Disable Right Click, Screenshots, etc.)
// Note: This runs globally. To limit to production only, verify import.meta.env.PROD
useContentProtection()

// Sidebar states
const isMobileMenuOpen = ref(false)
const isSidebarOpen = ref(true)

// UI states
const isLoading = ref(false)

// Check if current route is login page
const isLoginPage = computed(() => {
  return route.name === 'login'
})

// Toggle mobile menu
function toggleMobileMenu(value?: boolean) {
  isMobileMenuOpen.value = typeof value === 'boolean' ? value : !isMobileMenuOpen.value
}

// Toggle desktop sidebar
function toggleSidebar() {
  isSidebarOpen.value = !isSidebarOpen.value
  localStorage.setItem('sidebarOpen', isSidebarOpen.value.toString())
}



// Initialize sidebar state from localStorage
onMounted(() => {
  const savedState = localStorage.getItem('sidebarOpen')
  if (savedState !== null) {
    isSidebarOpen.value = savedState === 'true'
  }

  // Initialize theme
  const themeStore = useThemeStore()
  themeStore.init()
})

// Handle route changes for loading state
// Using router.afterEach is safer than beforeEach to avoid conflicts with auth guards
router.afterEach(() => {
  // Clear loading after navigation completes
  setTimeout(() => {
    isLoading.value = false
  }, 300)
})

// Watch route changes to show loading indicator
// This approach doesn't interfere with navigation guards
const unwatchRoute = watch(
  () => route.name,
  (newRoute, oldRoute) => {
    // Only show loading if this is an actual route change
    if (oldRoute && newRoute !== oldRoute) {
      isLoading.value = true
    }
  }
)

// Cleanup watcher on unmount
onUnmounted(() => {
  unwatchRoute()
})
</script>

<style>
/* Import Google Fonts */
/* Import Google Fonts handled in index.html for better performance */

body {
  font-family: 'Plus Jakarta Sans', sans-serif;
}

/* Page transitions */
.page-enter-active,
.page-leave-active {
  transition: all 0.3s ease-out;
}

.page-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.page-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.dark ::-webkit-scrollbar-thumb {
  background: #4b5563;
}

.dark ::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}
</style>
